import { noop } from "@/manager/utils";
import ControlledTable, { IColumn, ITableRowData } from "../../../manager/components/controlled-table/controlled-table";
import { useContext, useEffect, useState } from "react";
import { GrRevert } from "react-icons/gr";
import SimpleModal from "@/shared/components/simple-modal/simple-modal";
import Popup from "@/shared/components/popup/popup";
import { createUUID, validateLoadingStatusNew } from "@/shared/utils/utils";
import "./datamodel-table.scss";
import Tooltip from "@/shared/components/tooltip/tooltip";
import { Button, Form } from "react-bootstrap";
import { TbDatabaseImport } from "react-icons/tb";
import { failedToast, getAllData, postSingleNonTableImport } from "@/shared/utils/api";
import { useParams } from "react-router";
import { activeOptions, databaseTypes, fieldtypeOptions } from "@/shared/utils/static-values";
import { RenderFields } from "@/shared/utils/render-fields";
import { IFieldConfigDefinition } from "@/shared/definitions/field";
import useHttp from "@/shared/hooks/use-http";
import { AuthContext } from "@/shared/context/auth-context";
import Spinner from "@/shared/components/spinner/spinner";

interface IDatamodelTableProps {
  data: ITableRowData[];
  datamodelType: 'entity' | 'event' | 'alertcase';
  onDataUpdated: (data: ITableRowData[]) => void;
  onRevert: () => void;
  readonly: boolean;
}

const columns: IColumn[] = [
  {
    key: "input_field_name",
    displayName: "Input field name",
    dataType: {
      type: "text",
    },
    sortable: false,
  },
  {
    key: "field_name",
    displayName: "Name",
    dataType: {
      type: "text",
    },
    sortable: false,
  },
  {
    key: "field_datatype",
    displayName: "Fieldtype",
    dataType: {
      type: "enumeration",
      values: fieldtypeOptions,
    },
    sortable: false,
  },
  {
    key: "text_type",
    displayName: "Text type",
    dataType: {
      type: "enumeration",
      values: [
        { name: "Short text (max 250 characters)", id: "short_text" },
        { name: "Long text", id: "long_text" },
      ],
    },
    sortable: false,
  },
  {
    key: "primary_key",
    displayName: "Primary key",
    dataType: {
      type: "enumeration",
      values: activeOptions,
    },
    sortable: false,
  },
  {
    key: "null_values",
    displayName: "Null values",
    dataType: {
      type: "noDisplayCol",
    },
    sortable: false,
  },
  {
    key: "empty_values",
    displayName: "Empty values",
    dataType: {
      type: "noDisplayCol",
    },
    sortable: false,
  },
  {
    key: "unique_values",
    displayName: "Unique values",
    dataType: {
      type: "noDisplayCol",
    },
    sortable: false,
  }
];

const DatamodelTable = ({ datamodelType, data, onDataUpdated, onRevert, readonly }: IDatamodelTableProps) => {
  const emptyField = {
    id: "",
    input_field_name: "",
    field_name: "",
    field_datatype: ""
  }
  
  const params = useParams();
  const isNew = !params.id
  const authCtx = useContext(AuthContext);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [showDatamodelImportModal, setShowDatamodelImportModal] = useState(false);
  const [showEditFieldModal, setShowEditFieldModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<ITableRowData>(emptyField);
  const [fieldsChanged, setFieldsChanged] = useState(false);
  const [importModalData, setImportModalData] = useState<ITableRowData>({
    id: "",
    file: undefined,
    name: "",
    csv_separator: "",
    source_type: "",
    database_connection_id: "",
  });

  const {
    sendRequest: getDatabaseConnections,
    status: getDatabaseConnectionsStatus,
    data: getDatabaseConnectionsData
  } = useHttp(getAllData, false, authCtx.token, "databaseConnection");

  const {
    sendRequest: postDatamodelImport,
    status: postDatamodelImportStatus,
    data: postDatamodelImportData,
    error: postDatamodelImportError,
  } = useHttp(postSingleNonTableImport, false, authCtx.token, "event");

  useEffect(() => {
    getDatabaseConnections("database_connections");
  }, []);

  useEffect(() => {
    if (postDatamodelImportStatus === "completed" && !postDatamodelImportError && postDatamodelImportData) {
      onDataUpdated([...postDatamodelImportData.rowData]);
    }
  }, [postDatamodelImportError, postDatamodelImportStatus, postDatamodelImportData]);

  const handleOnSelectionChange = (selectedIds: string[]) => {
    setSelectedIds(selectedIds);
  };

  const handleOnDeleted = (deletedIds: string[]) => {
    const newData = data.filter(row => !deletedIds.includes(row.id as string));
    onDataUpdated([...newData]);
  }

  const handleImportModalChange = (value: string, key: string, id?: string) => {
    if (key === "database_connection") {
      setImportModalData((prevState) => ({
        ...prevState,
        database_connection: value,
        database_connection_id: id ? id : "",
      }));
    } else {
      setImportModalData((prevState) => ({
        ...prevState,
        [key]: value
      }));
    }
  }

  const renderExtraButtons = () => {
    if (readonly) {
      return undefined;
    }
    return <div>
      { isNew ?
      <Tooltip content={"Datamodel import"} direction="top" delay={350}>
        <Button onClick={() => setShowDatamodelImportModal(true)}>
          <TbDatabaseImport />
        </Button>
      </Tooltip>
      :
      <Tooltip content={"Revert all changes"} direction="top" delay={350}>
        <Button disabled={!fieldsChanged} onClick={() => setShowRevertModal(true)}>
          <GrRevert />
        </Button>
      </Tooltip>
      }
    </div>
  }

  const renderDatamodelImportModalBody = () => {
    const fields: IFieldConfigDefinition[] = [{
      key: "source_type",
      displayName: "Source type",
      dataType: {
        type: "enumeration",
        values: databaseTypes
      }
    }];

    if (importModalData.source_type && importModalData.source_type === "csv") {
      fields.push({
        key: "csv_separator",
        displayName: "Separator",
        dataType: {
          type: "text",
        },
        controlled: true,
        placeholder: "e.g. ,",
      });
    }

    if (importModalData.source_type && importModalData.source_type === "db") {
      const databaseConnections = getDatabaseConnectionsData && getDatabaseConnectionsData.length !== 0
        ? getDatabaseConnectionsData.map((dbOption: ITableRowData) => ({ id: dbOption.id, name: dbOption.name }))
        : [];
      fields.push(
        {
          key: "name",
          displayName: "Name",
          dataType: {
            type: "text",
          },
          placeholder: "Table name",
        },
        {
          key: "database_connection",
          displayName: "Database connection",
          dataType: {
            type: "reference",
            values: databaseConnections
          }
        });
    }

    return <div>
      <RenderFields
        fields={fields}
        data={{
          ...importModalData,
          csv_separator: importModalData?.csv_separator
            ? importModalData.csv_separator
            : importModalData?.csv_separator,
        }}
        handleOnChange={handleImportModalChange}
      />
      {importModalData.source_type &&
      importModalData.source_type === "csv" ? (
        <div className="csvFileInput">
          <Form.Control
            required={true}
            type={"file"}
            accept={".csv"}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setImportModalData({
                ...importModalData,
                file: e.target.files?.[0],
              })
            }
          />
        </div>
      ) : (
        ""
      )}
      {importModalData.source_type && (
        <div className="popupMessage">
          <span className="importantLine">Important:</span>
          {"The imported data will overwrite the current state."}
        </div>
      )}
    </div>
  }

  const handleDatamodelImportSubmit = () => {
    if (importModalData.source_type && importModalData.source_type === "csv") {
      const formData = new FormData();
      formData.append("file", importModalData.file);
      formData.append("csv_separator", importModalData.csv_separator);
      formData.append("source_type", importModalData.source_type);
      formData.append("record_id", "");
      formData.append("record_type", datamodelType);
      postDatamodelImport("datamodel_import", formData, "form");
    } else if (
      importModalData.source_type &&
      importModalData.source_type === "db"
    ) {
      postDatamodelImport("datamodel_import", {
        record_type: datamodelType,
        record_id: "",
        source_type: importModalData.source_type,
        database_connection_id: importModalData.database_connection_id,
        name: importModalData.name,
      });
    }
    setShowDatamodelImportModal(false);
  }

  return <div className="datamodelTable">
    {validateLoadingStatusNew([
      getDatabaseConnectionsStatus
    ]) && <Spinner />}
    {/* Revert Confirmation Modal */}
    <SimpleModal
      show={showRevertModal}
      handleModalClosed={() => setShowRevertModal(false)}
      handleSubmit={() => {
        onRevert();
        setFieldsChanged(false);
        setShowRevertModal(false);
      }}
      modalBody={<span> Are you sure you want to revert all changes?</span>}
      mode="Revert all changes"
      buttonText="Revert"
      showCancelButton={true}
    />
    <Popup
      mode={selectedRowData.id ? "Edit" : "New"}
      buttonText={selectedRowData.id ? "Save changes" : "Create"}
      colums={[...columns]}
      values={selectedRowData.id ? selectedRowData : {...emptyField, id: createUUID()}}
      show={showEditFieldModal}
      handlePopupClosed={(result) => {
        if (selectedRowData.id && result) {
          const updatedData = data.map(row => row.id === selectedRowData.id ? {...row, ...result} : row);
          const existingRow = data.find(row => row.id === selectedRowData.id);
          if (!existingRow) {
            failedToast("The field you are trying to edit does not exist, please try again.");
            return;
          }
          // Check if any property of the existing row has changed
          if (existingRow) {
            const hasChanged = Object.keys(existingRow).some(key => existingRow[key] !== result[key]);
            if (hasChanged) {
              setFieldsChanged(true);
              onDataUpdated(updatedData);
            }
          }
        } else if (result) {
          // Add new row
          if (data.some(row => row.input_field_name === result.input_field_name)) {
            failedToast("A field with the same input field name already exists.");
            return;
          }
          else {
            setFieldsChanged(true);
            onDataUpdated([...data, result]);
          }
        }
        setSelectedRowData({...emptyField});
        setShowEditFieldModal(false)
      }}
      rowsData={[]}
    />

    {/* Datamodel Import Modal */}
    <SimpleModal
      modalBody={renderDatamodelImportModalBody()}
      buttonText={"Generate"}
      mode={"Datamodel import"}
      handleModalClosed={() => setShowDatamodelImportModal(false)}
      handleSubmit={handleDatamodelImportSubmit}
      show={showDatamodelImportModal}
    />

    <ControlledTable
      data={data}
      readOnly={readonly}
      columns={columns}
      style="default"
      onRowClick={(id: string) => {
        const rowData = data.find(row => row.id === id);
        if (!rowData) {
          return;
        }
        setSelectedRowData(rowData);
        setShowEditFieldModal(true);
      }}
      onScroll={noop}
      selectionMode="multi"
      onSorted={noop}
      onNewClicked={() => setShowEditFieldModal(true)}
      onDeleted={handleOnDeleted}
      onSelectionChange={handleOnSelectionChange}
      preSelectedIds={selectedIds}
      extraButtons={renderExtraButtons()}
    />
  </div>;
}

export default DatamodelTable;
